import Image from 'next/image';
import {
    Dispatch,
    SetStateAction,
    useContext,
    useEffect,
    useState,
} from 'react';
import { getImageBasePath } from 'utils/generics';
import Header from 'widgets/header';
import * as Styles from './styles';
import Calendar from '@acko-tech/building-blocks.ui.calendar';
import { PrimaryButton } from '@acko-tech/building-blocks.ui.button';
import { ModalDesktop, ModalMobile } from '@acko-tech/building-blocks.ui.modal';
import { useViewport } from 'utils/responsive';
import {
    ICommissionSummaryRequest,
    IData,
    IFilterOptionsValue,
    IFiltersOptions,
    IHeader,
} from './types';
import { MPosContext } from 'utils/context';
import { downloadCommissionReport, getCommissionSummary } from './api';
import { getDD_MM_YYYY, snakeToCapitalized } from 'utils/date';
import PaginatedTable from './PaginatedTable';
import SnackBar from 'library/snackbar';

const CommissionListing = () => {
    const { partnerId } = useContext(MPosContext);
    const [dateRange, setDateRange] = useState({
        from: new Date(new Date().setMonth(new Date().getMonth() - 1)),
        end: new Date(),
    });
    const { isMobile } = useViewport();
    const [loader, setLoader] = useState<boolean>(false);
    const [filterClicked, setFilterClicked] = useState(false);
    const [selectedKey, setSelectedKey] = useState('group_by');
    const [selectedValue, setSelectedValue] = useState('DATE');
    const [filters, setFilters] = useState<IFiltersOptions[]>([]);
    const [page, setPage] = useState<number>(0);
    const [headers, setHeaders] = useState<IHeader[]>([]);
    const [data, setData] = useState<IData[]>([]);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [snackbar, setSnackbar] = useState({
        type: '',
        message: '',
    });
    const [progress, setProgress] = useState(0);
    const [isApiTriggered, setIsApiTriggered] = useState(false);
    const [calendarError, setCalendarError] = useState('');
    const [buttonLoader, setButtonLoader] = useState(false);

    const getSummary = async (page) => {
        try {
            setLoader(true);
            const request: ICommissionSummaryRequest = {
                from_date: getDD_MM_YYYY(dateRange.from),
                to_date: getDD_MM_YYYY(dateRange.end),
                page,
                [selectedKey]: selectedValue,
            };
            const data = await getCommissionSummary(request, partnerId);
            setFilters(data.filters);
            setHeaders(data.headers);
            setData(data.data);
            setTotalPages(data.total_pages);
            if (!data.status) {
                setSnackbar({
                    type: 'failure',
                    message: 'Failed to fetch commission',
                });
            } else {
                setSnackbar({
                    type: '',
                    message: '',
                });
            }
        } catch (e) {
            setSnackbar({
                type: 'failure',
                message: 'Failed to fetch commissions',
            });
        } finally {
            setLoader(false);
        }
    };

    // const clickApply = () => {
    //     setPage(0);
    //     getSummary(0);
    // };

    useEffect(() => {
        if (isApiTriggered) {
            setPage(0);
            getSummary(0);
        }
    }, [selectedValue]);

    useEffect(() => {
        if (!isApiTriggered) {
            getSummary(0);
            setIsApiTriggered(true);
        }
    }, []);

    console.log('Progress', progress);

    useEffect(() => {
        if (isApiTriggered) {
            if (dateRange.from.getTime() > dateRange.end.getTime()) {
                setCalendarError('Start Date cannot be greater than End date');
            } else {
                setCalendarError('');
                getSummary(0);
            }
        }
    }, [dateRange]);

    useEffect(() => {
        if (isApiTriggered) {
            getSummary(page);
        }
    }, [page]);

    const onChange = (e: Date, id: string) => {
        switch (id) {
            case 'from':
                setDateRange((state) => ({
                    ...state,
                    from: e,
                }));
                break;
            case 'end':
                setDateRange((state) => ({
                    ...state,
                    end: e,
                }));

                break;
            default:
        }
    };

    const Modal = isMobile ? ModalMobile : ModalDesktop;

    const valueSelected = () => {
        setFilterClicked(false);
    };

    const onNextPage = () => {
        setPage(page + 1);
    };

    const onPrevioudPage = () => {
        setPage(page - 1);
    };

    const handleDownload = async () => {
        setButtonLoader(true);

        setProgress(0); // Reset progress before starting

        const request: ICommissionSummaryRequest = {
            from_date: getDD_MM_YYYY(dateRange.from),
            to_date: getDD_MM_YYYY(dateRange.end),
            [selectedKey]: selectedValue,
            page,
        };

        const response = await downloadCommissionReport(
            request,
            partnerId,
            (progress) => {
                setProgress(progress);
            }
        );

        if (response.status && response.blobData) {
            const url = window.URL.createObjectURL(response.blobData);
            window.open(url, '_blank', 'noopener,noreferrer');
        } else {
            setSnackbar({
                type: 'failure',
                message: 'Failed to download the report.',
            });
        }
        setButtonLoader(false);
    };

    return (
        <>
            <Styles.Wrapper>
                <Header />
                <Styles.Hr />
                <CommissionListingHeader />
                <Styles.CommisionFilterWrapper>
                    <Styles.DropDownBox is_mobile={isMobile}>
                        <Styles.CalendarWrapper>
                            <Calendar
                                title={'From'}
                                defaultDate={dateRange.from}
                                onSelect={(date: Date) => {
                                    onChange(date, 'from');
                                }}
                                enableWindow={{
                                    start: new Date(
                                        new Date().setFullYear(
                                            new Date().getFullYear() - 100
                                        )
                                    ),
                                    current: dateRange.from,
                                    end: new Date(),
                                }}
                                isDesktop={!isMobile}
                            />
                            {calendarError && calendarError !== null && (
                                <Styles.ErrorText>
                                    {calendarError}
                                </Styles.ErrorText>
                            )}
                        </Styles.CalendarWrapper>
                        <Styles.CalendarWrapper>
                            <Calendar
                                title={'To'}
                                defaultDate={dateRange.end}
                                onSelect={(date: Date) => {
                                    onChange(date, 'end');
                                }}
                                enableWindow={{
                                    start: new Date(
                                        new Date().setFullYear(
                                            new Date().getFullYear() - 100
                                        )
                                    ),
                                    current: dateRange.end,
                                    end: new Date(),
                                }}
                                isDesktop={!isMobile}
                            />
                        </Styles.CalendarWrapper>
                    </Styles.DropDownBox>
                </Styles.CommisionFilterWrapper>
                <Styles.ListingWrapper>
                    {loader ? (
                        <Styles.LoaderBox
                            style={{
                                height: '100%',
                                display: 'grid',
                                placeItems: 'center',
                            }}
                        >
                            <Image
                                src={'/gif/pageLoader.gif'}
                                alt=""
                                height={'150px'}
                                width={'150px'}
                                style={{ margin: '0 auto' }}
                            />
                        </Styles.LoaderBox>
                    ) : (
                        data && (
                            <PaginatedTable
                                headers={headers}
                                data={data}
                                totalPages={totalPages}
                                onNext={onNextPage}
                                onBack={onPrevioudPage}
                                currentPage={page + 1}
                            />
                        )
                    )}
                </Styles.ListingWrapper>
                <Styles.ButtonWrapper>
                    <Styles.FilterOptionButtonWrapper
                        onClick={() => {
                            setFilterClicked(true);
                        }}
                    >
                        <Image
                            src={getImageBasePath('/icons/filter.svg')}
                            alt=""
                            width={'40px'}
                            height={'40px'}
                        />
                        Filter
                    </Styles.FilterOptionButtonWrapper>
                    <PrimaryButton
                        onClick={handleDownload}
                        loading={buttonLoader}
                    >
                        Download
                    </PrimaryButton>
                </Styles.ButtonWrapper>
            </Styles.Wrapper>

            <Modal
                show={filterClicked}
                urlhash={''}
                onHide={() => {
                    setFilterClicked(false);
                }}
                padding={'20px'}
                wrapperStyles={`border-radius: 16px 16px 0px 0px;`}
                showOverlayBackground={true}
                hideCloseButton={false}
            >
                <FilterOptions
                    filters={filters}
                    selectedKey={selectedKey}
                    selectedValue={selectedValue}
                    setSelectedKey={setSelectedKey}
                    setSelectedValue={setSelectedValue}
                    close={valueSelected}
                />
            </Modal>
            <SnackBar
                {...snackbar}
                handleSnakbarComplete={() =>
                    setSnackbar({ type: '', message: '' })
                }
            />
        </>
    );
};

const CommissionListingHeader = () => {
    return (
        <Styles.Header>
            <Styles.HeaderIcon>
                <Image
                    src={getImageBasePath('/icons/commissions_light.svg')}
                    alt=""
                    width={'30px'}
                    height={'30px'}
                />
            </Styles.HeaderIcon>
            <Styles.HeaderTextWrapper>
                <Styles.HeaderText>Sales Report</Styles.HeaderText>
            </Styles.HeaderTextWrapper>
        </Styles.Header>
    );
};

interface FilterOptionsIProps {
    filters: IFiltersOptions[];
    selectedKey: string;
    selectedValue: string;
    setSelectedKey: Dispatch<SetStateAction<string>>;
    setSelectedValue: Dispatch<SetStateAction<string>>;
    close: () => void;
}

const FilterOptions: React.FC<FilterOptionsIProps> = (props) => {
    const {
        filters,
        selectedKey,
        selectedValue,
        setSelectedKey,
        setSelectedValue,
        close,
    } = props;

    const [filterKey, setFilterKey] = useState(selectedKey);
    const [filterValue, setFilterValue] = useState<string>(selectedValue);

    const [values, setValues] = useState<IFilterOptionsValue[]>([]);
    const keys = filters?.map((filter) => filter.key);

    useEffect(() => {
        if (filterKey !== '') {
            Object.values(filters).forEach((filter: IFiltersOptions) => {
                if (filter?.key === filterKey) {
                    setValues(filter.values);
                    setSelectedKey(filterKey);
                }
            });
        }
    }, [filterKey]);

    return (
        <Styles.FilterOption>
            <Styles.FilterOptionKeys>
                {keys.map((key) => {
                    return (
                        <Styles.FilterOptionKeyBox
                            key={key}
                            onClick={() => {
                                setFilterKey(key);
                            }}
                        >
                            <Styles.FilterOptionKey
                                style={{
                                    color:
                                        filterKey === key ? 'green' : 'black',
                                }}
                            >
                                {snakeToCapitalized(key)}
                            </Styles.FilterOptionKey>
                        </Styles.FilterOptionKeyBox>
                    );
                })}
            </Styles.FilterOptionKeys>
            <Styles.FilterOptionValues>
                {values.map((value) => {
                    return (
                        <Styles.FilterOptionValueBox
                            key={value.key}
                            onClick={() => {
                                setFilterValue(value.key);
                                setSelectedValue(value.key);
                                close();
                            }}
                        >
                            <Styles.FilterOptionCheckBox>
                                <Styles.FilterOptionCheckInnerBox
                                    style={{
                                        backgroundColor:
                                            value.key === filterValue
                                                ? 'green'
                                                : 'transparent',
                                    }}
                                />
                            </Styles.FilterOptionCheckBox>

                            <Styles.FilterOptionValue
                                style={{
                                    color:
                                        value.key === filterValue
                                            ? 'green'
                                            : 'black',
                                }}
                            >
                                {value.display_name}
                            </Styles.FilterOptionValue>
                        </Styles.FilterOptionValueBox>
                    );
                })}
            </Styles.FilterOptionValues>
        </Styles.FilterOption>
    );
};

export default CommissionListing;
